<template>
    <div>
        <loader v-if="isLoading" />
        <table v-else class="table b-table list table-hover table-borderless">
            <thead>
                <tr>
                    <th>Item:</th>
                    <th>Patient:</th>
                    <th>Quantity:</th>
                    <th>Reason:</th>
                    <th>Note:</th>
                    <th>Line Total:</th>
                    <th class="text-right">Print:</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(declinedItem, i) in declinedItems" :key="i">
                    <td>
                        <span
                            v-if="
                                declinedItem.item &&
                                declinedItem.item.type == 12
                            "
                        >
                            {{ declinedItem.item.name }}
                        </span>
                        <span v-else-if="declinedItem.item">
                            {{ declinedItem.item.name }} ({{
                                _setDateDisplay(declinedItem.date)
                            }})
                        </span>
                    </td>
                    <td>
                        <patient-name
                            :use-router="USE_PATIENT_MODULE_VUE"
                            :patient="declinedItem.patient"
                        />
                    </td>
                    <td>
                        {{ displayQuantity(declinedItem) }}
                    </td>
                    <td>
                        <span v-if="declinedItem.decline_type">
                            {{ declinedItem.decline_type.value }}</span
                        >
                    </td>
                    <td>
                        {{ declinedItem.decline_note }}
                    </td>
                    <td>
                        {{ displayLineTotal(declinedItem) }}
                    </td>
                    <td class="text-right">
                        {{ declinedItem.print_on_estimate == 1 ? "Yes" : "No" }}
                    </td>
                    <td>
                        <!-- TODO : insert declined item action -->
                        <i
                            v-if="
                                _hasAccess('billing.estimate.item.insert') &&
                                !estimate.isapprove &&
                                isactiveClient
                            "
                            class="cus-page-add cursor-pointer"
                            v-b-tooltip.hover
                            title="Insert Item"
                            @click="insertItem(declinedItem)"
                        ></i>
                        <!-- TODO : TBC need gear icon ? it is always hidden in apps -->
                    </td>
                </tr>
                <tr v-if="declinedItems.length === 0">
                    <td colspan="9"><i>No records available</i></td>
                </tr>
            </tbody>
        </table>

        <b-pagination
            v-if="meta && meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getList"
        />
        <modal-insert-estimate-item
            ref="modal-insert-estimate-item"
            modal-id="modal-insert-estimate-item"
            @reload="onSuccess"
        />
    </div>
</template>

<script>
/**
 * @name EstimateDeclinedList
 * @description List of declined items for estimate page
 * @see apps/application/views/staff/billing/invoice/estimate/sub/item/decline_list.php
 */
import Loader from "@/components/elements/loader-list";
import EstimateDeclinedService from "@/services/estimate/estimate-declined";
import PatientName from "@/components/commons/patient-name";
import CITEMTYPES from "@/constants/item-types";
import BILLINGS from "@/constants/billings";
import RELATIONS from "@/constants/relations";

export default {
    mixins: {},
    components: {
        Loader,
        PatientName,
        ModalInsertEstimateItem: () =>
            import("@/components/estimate-declined/modal-insert-estimate-item")
    },
    props: {
        estimate: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            declinedItems: [],
            meta: null,
            BILLINGS
        };
    },
    computed: {
        isactiveClient() {
            let client =
                this.estimate && this.estimate.client
                    ? this.estimate.client.single_business_actor
                    : null;

            if (client) {
                return (
                    client.isactive &&
                    client.relation_type == RELATIONS.BUSINESS_ACTOR.CLIENT
                );
            }

            return true;
        }
    },
    async mounted() {
        this.getList();
    },
    methods: {
        getList(page = 1) {
            let self = this;
            self.invoiceItems = [];
            self.isLoading = true;
            const params = {
                page: page,
                perPage: self.PER_PAGE
            };
            EstimateDeclinedService.getDeclinedItems(this.estimate.id, params)
                .then(res => {
                    if (res) {
                        self.declinedItems = res.resources;
                        self.meta = res.meta;
                    } else {
                        throw "Error : Unable to retrieve declined item list";
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to retrieve declined item list",
                        "danger"
                    );
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        onSuccess() {
            this.getList();
            this.$emit("reload-estimate-details");
        },
        displayQuantity(estimateItem) {
            const uom = estimateItem.item_uom
                ? estimateItem.item_uom.value
                : "";
            if (estimateItem.quantity_max > -1) {
                return `${estimateItem.quantity_min} - ${estimateItem.quantity_max} ${uom}`;
            }
            return `${estimateItem.quantity_min} ${uom}`;
        },
        displayLineTotal(estimateItem) {
            let lineTotalMin = this.calculateLineTotal(
                estimateItem,
                estimateItem.discount_min,
                estimateItem.price_min,
                estimateItem.tax_min
            );

            if (estimateItem.quantity_max > -1) {
                let lineTotalMax = this.calculateLineTotal(
                    estimateItem,
                    estimateItem.discount_max,
                    estimateItem.price_max,
                    estimateItem.tax_max
                );
                return (
                    this._toCurrency(lineTotalMin) +
                    " - " +
                    this._toCurrency(lineTotalMax)
                );
            } else {
                return this._toCurrency(lineTotalMin);
            }
        },
        calculateLineTotal(estimateItem, discount, price, tax) {
            let amount = 0;
            let priceAndFee = estimateItem.print_on_estimate
                ? parseFloat(price) + parseFloat(estimateItem.fee)
                : 0;

            if (estimateItem.print_on_estimate) {
                if (estimateItem.item) {
                    if (
                        estimateItem.item.type == CITEMTYPES.TYPES.TYPE_DISCOUNT
                    ) {
                        amount = parseFloat(discount);
                    } else {
                        amount =
                            parseFloat(priceAndFee) -
                            parseFloat(discount) +
                            parseFloat(tax);
                    }
                } else {
                    amount =
                        parseFloat(priceAndFee) -
                        parseFloat(discount) +
                        parseFloat(tax);
                }
            } else {
                amount = 0;
            }

            return amount;
        },
        insertItem(declineItem) {
            this.$refs["modal-insert-estimate-item"].open(declineItem);
        }
    }
};
</script>
